import {
  MODES,
  SEL,
  EVENTS,
  TIMING,
  debug,
  debounce,
  cssEscape,
  safeAsync,
  ServerSelectionState,
  normalizeMode,
  normalizeStore,
  readStoreFromItem,
  createSyncQueue,
  updateCartAttributes,
  refreshCartDrawer,
  getStoreCartStock,
  getDeliveryCartStock,
  createEmptyCartStockSummary,
  parseInventory,
  $,
  setStoreView,
  showPanel,
  hidePanel,
  activateTab,
  createStoreList,
  dispatchStoreChanged,
  analytics,
} from 'store-selector-core';
const PENDING_CONCILIATION_STORAGE_KEY = 'cart-click-collect-conciliation-pending';
const SYNC_IN_PROGRESS_KEY = 'cart-click-collect-sync-active';

/* The sync flag only has meaning within a single page lifecycle. If a sync was
   interrupted by a navigation or crash, the sessionStorage flag would otherwise
   leak into the next page load and suppress conciliation auto-open. */
let hasClearedStaleSyncFlag = false;

class CartInventory {
  /** @param {{ getCartDrawer: () => Element|null, getStorePanel: () => Element|null }} deps */
  constructor({ getCartDrawer, getStorePanel }) {
    this.getCartDrawer = getCartDrawer;
    this.getStorePanel = getStorePanel;
    this.inventoryData = null;
  }

  invalidate() {
    this.inventoryData = null;
  }

  #parse() {
    const cartDrawer = this.getCartDrawer();
    this.inventoryData = parseInventory(cartDrawer?.querySelector('[data-role="click-collect-inventory-data"]'));
  }

  #ensureParsed() {
    if (!this.inventoryData) this.#parse();
  }

  getInventoryLines() {
    this.#ensureParsed();
    return this.inventoryData?.lines ?? [];
  }

  getStoreCartStock(storeId) {
    this.#ensureParsed();
    try {
      return getStoreCartStock(this.inventoryData, storeId) || createEmptyCartStockSummary();
    } catch (error) {
      debug.error('cart:get_store_cart_stock', error, { storeId });
      return createEmptyCartStockSummary();
    }
  }

  getDeliveryCartStock() {
    this.#ensureParsed();
    try {
      return getDeliveryCartStock(this.inventoryData) || createEmptyCartStockSummary();
    } catch (error) {
      debug.error('cart:get_delivery_cart_stock', error);
      return createEmptyCartStockSummary();
    }
  }

  /**
   * Reconcile cart lines: reduce/remove items unavailable in the target mode+store.
   *
   * @param {{ mode: string, storeId?: string }} target
   */
  async reconcile(target) {
    const lines = this.getInventoryLines();
    if (!lines.length) return true;

    const targetMode = target?.mode === MODES.CLICK_COLLECT ? MODES.CLICK_COLLECT : MODES.DELIVERY;
    const targetStoreId = targetMode === MODES.CLICK_COLLECT ? String(target?.storeId || '') : '';
    const updates = [];
    const remainingByVariant = new Map();
    for (const line of lines) {
      const variantKey = String(line.variantId ?? line.id ?? '');
      const requestedQuantity = Number(line.quantity) || 0;
      if (!remainingByVariant.has(variantKey)) {
        const tagSupported = targetMode === MODES.CLICK_COLLECT ? line.supportsCnC !== false : line.supportsDelivery !== false;
        const availableQuantity = tagSupported
          ? targetMode === MODES.CLICK_COLLECT
            ? Number(line.inventories?.[targetStoreId] ?? line.inventories?.[Number(targetStoreId)] ?? 0)
            : Number(line.deliveryInventoryQuantity ?? line.delivery_inventory_quantity ?? 0)
          : 0;
        remainingByVariant.set(variantKey, Math.max(0, availableQuantity || 0));
      }

      const remainingQuantity = Math.max(0, Number(remainingByVariant.get(variantKey) ?? 0));
      const nextQuantity = Math.min(requestedQuantity, remainingQuantity);
      remainingByVariant.set(variantKey, Math.max(0, remainingQuantity - nextQuantity));
      if (nextQuantity === requestedQuantity) continue;

      const lineKey = String(line.lineKey || '').trim();
      updates.push({
        lineIndex: Number(line.lineIndex) || 0,
        lineKey,
        quantity: nextQuantity,
        payload: lineKey ? { id: lineKey, quantity: nextQuantity } : { line: line.lineIndex, quantity: nextQuantity },
      });
    }

    if (!updates.length) return true;

    const sendReconcileBatch = async () => {
      if (!updates.every((change) => change.lineKey)) return false;

      const result = await safeAsync('reconcile:batch', async () => {
        const batchUpdates = updates.reduce((payload, change) => {
          payload[change.lineKey] = change.quantity;
          return payload;
        }, {});
        const response = await fetch(window.theme.routes.cartUpdate, {
          method: 'POST',
          headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
          body: JSON.stringify({ updates: batchUpdates }),
        });
        if (!response.ok) {
          let body = '';
          try {
            body = await response.text();
          } catch {
            /* no-op */
          }

          debug.error('reconcile:batch', 'non_ok_response', {
            status: response.status,
            body: body.slice(0, 400),
          });
          throw new Error(`reconcile_batch_failed_${response.status}`);
        }
      });

      if (!result.ok) debug.error('reconcile:batch:exception', result.error);

      return result.ok;
    };

    const sendReconcileChange = async (change) => {
      const result = await safeAsync('reconcile:line', async () => {
        const response = await fetch(window.theme.routes.cartChange, {
          method: 'POST',
          headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
          body: JSON.stringify(change.payload),
        });
        if (!response.ok) {
          let body = '';
          try {
            body = await response.text();
          } catch {
            /* no-op */
          }

          debug.error('reconcile:line', 'non_ok_response', {
            change,
            status: response.status,
            body: body.slice(0, 400),
          });
          throw new Error(`reconcile_failed_${response.status}`);
        }
      });
      if (!result.ok) {
        debug.error('reconcile:line:exception', result.error, { change });
        return false;
      }

      return true;
    };

    if (await sendReconcileBatch()) return true;

    /* Shopify's /cart/change mutates the cart version on every call, so
       concurrent requests always 409. Run all changes sequentially. */
    for (const change of updates) {
      const ok = await sendReconcileChange(change);
      if (!ok) return false;
    }

    return true;
  }
}

class StoreSelectorCart extends HTMLElement {
  constructor() {
    super();
    this.state = new ServerSelectionState();
    this.syncQueue = createSyncQueue();
    this.isSyncingCart = false;
    this.isConfirmLoading = false;
    this.notificationTimer = null;
    this.isValidatingCheckout = false;
    this.allowNextCheckoutSubmit = false;
    this.cartDrawer = null;
    this.storePanel = null;
    this.conciliationPanel = null;
    this.pendingConciliationMode = null;
    this.pendingConciliationStore = null;
    this.reopenConciliationAfterStoreSelector = false;
    this.storeSelectorOpenedFromTabSwitch = false;
    this.selectorOwnsCartDrawer = false;
    this.pendingTabSwitch = null;
    this.pendingTabFocusMode = null;
    this.boundStorePanel = null;
    this.boundConciliationPanel = null;
    this.boundCartDrawer = null;
    this.list = createStoreList({
      getPanel: () => this.storePanel,
      isInStock: (item) => {
        if (!item) return true;

        /* Prefer the live inventory JSON (refreshed on every cart change) over
           server-rendered card datasets, which can be stale after quantity edits. */
        const storeId = String(item.dataset.storeId || '').trim();
        if (storeId && this.inventory.getInventoryLines().length > 0) {
          return this.#isStoreCartAllInStock(storeId);
        }

        if (item.dataset.cartAllInStock !== undefined) return item.dataset.cartAllInStock === 'true';

        if (item.dataset.available !== undefined) return item.dataset.available === 'true';

        const stock = item.querySelector('[data-role="store-stock"]');
        return !stock || !stock.classList.contains('store-selector__stock--out-of-stock');
      },
    });
    this.inventory = new CartInventory({
      getCartDrawer: () => this.cartDrawer,
      getStorePanel: () => this.storePanel,
    });
    this.#applySearchDebounced = debounce((value) => this.list.applySearch(value), TIMING.searchDebounce);
  }

  connectedCallback() {
    if (!hasClearedStaleSyncFlag) {
      hasClearedStaleSyncFlag = true;
      this.#clearSyncFlag();
    }

    this.#disconnect();
    this.#refreshDomRefs({ preserveListState: false, rebindPanels: false });
    this.state.restore(this.#getServerSelection());
    this.#syncStateFromRenderedCart();
    this.#bindEvents();
    this.#syncUI();
    this.#consumeQueuedNotification();
    this.#consumeQueuedConciliation();
    this.#openConciliationIfNeeded();
    Promise.resolve().then(() => {
      if (!this.isConnected) return;

      this.#refreshDomRefs({ preserveListState: true, rebindPanels: true });
      this.#consumeQueuedConciliation();
      this.#syncUI();
      this.#openConciliationIfNeeded();
    });
  }

  disconnectedCallback() {
    this.#disconnect();
  }

  #disconnect() {
    this.#unbindEvents();
    clearTimeout(this.notificationTimer);
  }

  #getServerSelection() {
    return {
      committed: this.dataset.cartCommitted !== 'false',
      mode: this.dataset.fulfillmentMode || MODES.DELIVERY,
      store: {
        id: this.dataset.storeId,
        name: this.dataset.storeName || '',
        address: this.dataset.storeAddress || '',
        phone: this.dataset.storePhone || '',
        salonPhone: this.dataset.salonPhone || '',
      },
    };
  }

  #getRenderedStore(storeId) {
    const normalizedStoreId = String(storeId || '').trim();
    if (!normalizedStoreId) return null;

    return normalizeStore(
      this.list.getStoreById(normalizedStoreId, { id: normalizedStoreId }) || {
        id: normalizedStoreId,
        name: '',
        address: '',
      },
    );
  }

  #getRenderedSelection() {
    if (this.dataset.fulfillmentMode === MODES.MIXED) {
      return { mode: MODES.MIXED, store: null };
    }

    const lines = this.inventory.getInventoryLines();
    if (!lines.length) return null;

    const modes = new Set();
    const stores = new Map();
    for (const line of lines) {
      const lineMode = line?.lineFulfillmentMode === MODES.CLICK_COLLECT ? MODES.CLICK_COLLECT : MODES.DELIVERY;
      modes.add(lineMode);
      if (lineMode !== MODES.CLICK_COLLECT) continue;

      const store = this.#getRenderedStore(line?.selectedStoreId);
      if (!store?.id || stores.has(store.id)) continue;

      stores.set(store.id, store);
    }

    if (modes.size > 1 || stores.size > 1) {
      return { mode: MODES.MIXED, store: null };
    }

    if (modes.has(MODES.CLICK_COLLECT)) {
      return { mode: MODES.CLICK_COLLECT, store: stores.values().next().value || null };
    }

    return { mode: MODES.DELIVERY, store: null };
  }

  #syncStateFromRenderedCart() {
    const renderedSelection = this.#getRenderedSelection();
    if (!renderedSelection) return;

    this.state.mode = normalizeMode(renderedSelection.mode);
    this.state.store = this.state.getMode() === MODES.CLICK_COLLECT ? normalizeStore(renderedSelection.store) : null;
    this.state.pendingStore = null;
    this.state.setServerSelection(this.state.getMode(), this.state.store);
  }

  #hasCartItems() {
    return Boolean(this.cartDrawer?.querySelector('cart-items[data-empty="false"]'));
  }

  #serializeCartLines(lines, mapping) {
    return (lines || [])
      .map((line, index) =>
        JSON.stringify({
          key: String(mapping.key(line, index) || `line-${index + 1}`),
          quantity: Math.max(0, Number(mapping.quantity(line, index)) || 0),
          mode: mapping.mode(line, index) === MODES.CLICK_COLLECT ? MODES.CLICK_COLLECT : MODES.DELIVERY,
          storeId: String(mapping.storeId(line, index) || ''),
        }),
      )
      .sort();
  }

  #buildRenderedCartSnapshot() {
    const renderedMode = this.state.getMode();
    const renderedStoreId = renderedMode === MODES.CLICK_COLLECT ? this.state.getSelectedStore()?.id || '' : '';
    const lines = this.inventory.getInventoryLines();
    return {
      mode: renderedMode,
      storeId: renderedStoreId,
      lineCount: lines.length,
      totalQuantity: lines.reduce((total, line) => total + Math.max(0, Number(line?.quantity) || 0), 0),
      lines: this.#serializeCartLines(lines, {
        key: (line) => line?.lineKey,
        quantity: (line) => line?.quantity,
        mode: (line) => line?.lineFulfillmentMode,
        storeId: (line) => line?.selectedStoreId,
      }),
    };
  }

  #buildLiveCartSnapshot(cart) {
    const fulfillmentUtil = window.theme?.fulfillmentModeUtil;
    const fallbackMode = fulfillmentUtil?.getCartFallbackMode?.(cart) || MODES.DELIVERY;
    const resolvedMode = fulfillmentUtil?.getResolvedCartMode?.(cart) || MODES.DELIVERY;
    const resolvedStore = fulfillmentUtil?.getStoreFromCart?.(cart, resolvedMode) || null;
    const useCartLevelFulfillment = resolvedMode === MODES.CLICK_COLLECT || resolvedMode === MODES.DELIVERY;
    const items = Array.isArray(cart?.items) ? cart.items : [];
    return {
      mode: resolvedMode,
      storeId: resolvedMode === MODES.CLICK_COLLECT ? String(resolvedStore?.id || '') : '',
      lineCount: items.length,
      totalQuantity: items.reduce((total, item) => total + Math.max(0, Number(item?.quantity) || 0), 0),
      lines: this.#serializeCartLines(items, {
        key: (item) => item?.key,
        quantity: (item) => item?.quantity,
        mode: (item) => (useCartLevelFulfillment ? resolvedMode : fulfillmentUtil?.getLineFulfillmentMode?.(item, fallbackMode)),
        storeId: (item) => {
          const lineMode = useCartLevelFulfillment ? resolvedMode : fulfillmentUtil?.getLineFulfillmentMode?.(item, fallbackMode);
          if (useCartLevelFulfillment && lineMode === MODES.CLICK_COLLECT) return resolvedStore?.id || '';

          return fulfillmentUtil?.getStoreFromLineItem?.(item)?.id || (lineMode === MODES.CLICK_COLLECT ? resolvedStore?.id : '');
        },
      }),
    };
  }

  async #getLiveCartComparison() {
    const renderedSnapshot = this.#buildRenderedCartSnapshot();
    const liveCart = await this.#fetchLiveCart();
    const liveSnapshot = this.#buildLiveCartSnapshot(liveCart);
    return {
      liveCart,
      renderedSnapshot,
      liveSnapshot,
      matches: JSON.stringify(liveSnapshot) === JSON.stringify(renderedSnapshot),
    };
  }

  async #fetchLiveCart() {
    const response = await fetch(`${window.theme.routes.cart}.js`, {
      headers: { Accept: 'application/json' },
    });
    if (!response.ok) throw new Error(`cart_fetch_failed:${response.status}`);

    return response.json();
  }

  #getCheckoutForm() {
    const cartDrawerForm = this.cartDrawer?.querySelector('#cart-drawer-form');
    if (cartDrawerForm instanceof HTMLFormElement) return cartDrawerForm;

    return null;
  }

  #syncStateFromLiveCart(cart) {
    const fulfillmentUtil = window.theme?.fulfillmentModeUtil;
    const liveMode = fulfillmentUtil?.getResolvedCartMode?.(cart) || MODES.DELIVERY;
    const liveStore = fulfillmentUtil?.getStoreFromCart?.(cart, liveMode) || null;
    this.state.mode = normalizeMode(liveMode);
    this.state.store = this.state.getMode() === MODES.CLICK_COLLECT ? normalizeStore(liveStore) : null;
    this.state.pendingStore = null;
    this.state.setServerSelection(this.state.getMode(), this.state.store);
  }

  async #refreshCartForCheckoutBlock(liveCart) {
    this.#syncStateFromLiveCart(liveCart);
    this.#syncUI();
    await refreshCartDrawer();
    if (!this.isConnected) return;

    this.#openConciliationIfNeeded();
  }

  async #validateCheckoutAgainstLiveCart() {
    const { liveCart, matches } = await this.#getLiveCartComparison();
    this.#syncStateFromLiveCart(liveCart);
    if (!matches) {
      await this.#refreshCartForCheckoutBlock(liveCart);
      return false;
    }

    const liveMode = this.state.getMode();
    if (liveMode === MODES.MIXED) {
      await this.#refreshCartForCheckoutBlock(liveCart);
      return false;
    }

    if (liveMode === MODES.CLICK_COLLECT && !this.state.getSelectedStore()?.id) {
      await this.#refreshCartForCheckoutBlock(liveCart);
      return false;
    }

    this.#syncUI();
    return true;
  }

  async #continueCheckoutSubmit() {
    this.allowNextCheckoutSubmit = false;

    const checkoutUrl = window.theme?.routes?.checkout || '/checkout';

    window.location.assign(checkoutUrl);
  }

  async #runCheckoutValidation({ form, submitter } = {}) {
    if (!form || !submitter) return;

    if (this.isValidatingCheckout) return;

    this.isValidatingCheckout = true;
    submitter.classList.add('is-loading');
    this.#updateCheckoutState();
    try {
      const canCheckout = await this.#validateCheckoutAgainstLiveCart();

      if (canCheckout) {
        await this.#continueCheckoutSubmit();
        return;
      }
    } catch (error) {
      debug.error('cart:checkout_validation:exception', error);
      await refreshCartDrawer();
    } finally {
      this.isValidatingCheckout = false;
      submitter.classList.remove('is-loading');
      this.#updateCheckoutState();
    }
  }

  #openConciliationIfNeeded() {
    if (this.#isSyncFlagActive()) return;

    if (!this.#hasCartItems()) return;

    if (!this.conciliationPanel || !this.storePanel) return;

    if (!this.conciliationPanel.hidden || !this.storePanel.hidden) return;

    if (this.state.getMode() === MODES.MIXED) {
      this.#openConciliationPanel();
      return;
    }

    if (this.state.getMode() === MODES.CLICK_COLLECT && !this.state.getSelectedStore()?.id) {
      this.#openStoreSelector();
    }
  }

  #setSyncFlag() {
    try {
      window.sessionStorage.setItem(SYNC_IN_PROGRESS_KEY, '1');
    } catch {
      /* no-op */
    }
  }

  #clearSyncFlag() {
    try {
      window.sessionStorage.removeItem(SYNC_IN_PROGRESS_KEY);
    } catch {
      /* no-op */
    }
  }

  #isSyncFlagActive() {
    try {
      return window.sessionStorage.getItem(SYNC_IN_PROGRESS_KEY) === '1';
    } catch {
      return false;
    }
  }

  #refreshDomRefs({ preserveListState = true, rebindPanels = false } = {}) {
    this.#unbindPanelEvents();
    this.cartDrawer = document.getElementById('cart-drawer');
    this.storePanel = this.cartDrawer?.querySelector(SEL.cartStorePanel) || null;
    this.conciliationPanel = this.cartDrawer?.querySelector(SEL.cartConciliationPanel) || null;
    if (this.storePanel) {
      this.list.attach(this.storePanel, { preserveState: preserveListState });
    }

    if (rebindPanels) this.#bindPanelEvents();
  }

  #applySearchDebounced;

  #storeResolver = (storeId, fallback) => this.list.getStoreById(storeId, fallback);

  #bindEvents() {
    this.addEventListener('click', this.#onRootClick);
    this.addEventListener('keydown', this.#onRootKeydown);
    this.addEventListener('on:tab:activated', this.#onTabActivated);
    document.addEventListener(EVENTS.SECTIONS_UPDATED, this.#onSectionsUpdated);
    document.addEventListener(EVENTS.OPEN_SELECTOR, this.#onOpenStoreSelectorEvent);
    this.#bindPanelEvents();
  }

  #unbindEvents() {
    this.removeEventListener('click', this.#onRootClick);
    this.removeEventListener('keydown', this.#onRootKeydown);
    this.removeEventListener('on:tab:activated', this.#onTabActivated);
    document.removeEventListener(EVENTS.SECTIONS_UPDATED, this.#onSectionsUpdated);
    document.removeEventListener(EVENTS.OPEN_SELECTOR, this.#onOpenStoreSelectorEvent);
    this.#unbindPanelEvents();
  }

  #bindPanelEvents() {
    if (this.storePanel) {
      this.boundStorePanel = this.storePanel;
      this.boundStorePanel.addEventListener('change', this.#onDrawerChange);
      this.boundStorePanel.addEventListener('click', this.#onDrawerClick);
      this.boundStorePanel.addEventListener('input', this.#onDrawerInput);
    }

    if (this.conciliationPanel) {
      this.boundConciliationPanel = this.conciliationPanel;
      this.boundConciliationPanel.addEventListener('change', this.#onConciliationChange);
      this.boundConciliationPanel.addEventListener('click', this.#onConciliationClick);
    }

    if (this.cartDrawer) {
      this.boundCartDrawer = this.cartDrawer;
      this.boundCartDrawer.addEventListener('keyup', this.#onCartDrawerKeyup, true);
      this.boundCartDrawer.addEventListener('click', this.#onCartSummaryClick, true);
      this.boundCartDrawer.addEventListener('submit', this.#onCartFormSubmit, true);
      this.boundCartDrawer.addEventListener('on:cart-drawer:after-close', this.#onCartDrawerAfterClose);
    }
  }

  #unbindPanelEvents() {
    if (this.boundStorePanel) {
      this.boundStorePanel.removeEventListener('change', this.#onDrawerChange);
      this.boundStorePanel.removeEventListener('click', this.#onDrawerClick);
      this.boundStorePanel.removeEventListener('input', this.#onDrawerInput);
      this.boundStorePanel = null;
    }

    if (this.boundConciliationPanel) {
      this.boundConciliationPanel.removeEventListener('change', this.#onConciliationChange);
      this.boundConciliationPanel.removeEventListener('click', this.#onConciliationClick);
      this.boundConciliationPanel = null;
    }

    if (this.boundCartDrawer) {
      this.boundCartDrawer.removeEventListener('keyup', this.#onCartDrawerKeyup, true);
      this.boundCartDrawer.removeEventListener('click', this.#onCartSummaryClick, true);
      this.boundCartDrawer.removeEventListener('submit', this.#onCartFormSubmit, true);
      this.boundCartDrawer.removeEventListener('on:cart-drawer:after-close', this.#onCartDrawerAfterClose);
      this.boundCartDrawer = null;
    }
  }

  #onTabActivated = (evt) => {
    const tab = evt.detail?.tab;
    if (tab) this.#handleTabClick(tab);
  };

  #onRootKeydown = (evt) => {
    if (!['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Home', 'End'].includes(evt.key)) return;

    if (!evt.target.closest('[role="tab"]')) return;

    this.pendingTabFocusMode = this.querySelector('[role="tab"][aria-selected="true"]')?.dataset.fulfillment || null;
  };

  #onRootClick = (evt) => {
    if (evt.defaultPrevented) return;

    const tab = evt.target.closest('[role="tab"]');
    if (tab) {
      /* When the tabbed-content component is upgraded it re-dispatches user
         activations (click and keyboard) as on:tab:activated, which is handled
         above. Only route raw clicks when that component is unavailable. */
      const tabbedContent = tab.closest('tabbed-content');
      if (!tabbedContent || typeof tabbedContent.activateTab !== 'function') this.#handleTabClick(tab);

      return;
    }

    if (evt.target.closest('[data-action="change-store"]') || evt.target.closest('[data-action="open-store-selector"]')) {
      this.storeSelectorOpenedFromTabSwitch = false;
      this.#openStoreSelector();
      return;
    }

    if (evt.target.closest('[data-action="open-store-selector-filtered"]')) {
      this.storeSelectorOpenedFromTabSwitch = false;
      this.list.stockFilterEnabled = true;
      this.#openStoreSelector();
    }
  };

  #onDrawerChange = (evt) => {
    const stockToggle = evt.target.closest(SEL.stockFilterToggle);
    if (stockToggle) {
      this.list.setStockFilter(stockToggle.checked);
      return;
    }

    const radio = evt.target.closest(SEL.storeRadio);
    if (radio) this.#handleStoreRadioChange(radio);
  };

  #onDrawerClick = (evt) => {
    if (this.isSyncingCart) return;

    if (evt.target.closest(SEL.searchTriggerLocation)) {
      evt.preventDefault();
      this.list.requestGeolocation();
      return;
    }

    if (evt.target.closest(SEL.confirmStore)) {
      void this.#confirmPendingStore();
      return;
    }

    if (evt.target.closest(SEL.cancelStore) || evt.target.closest('[data-action="close-store-selector"]')) {
      this.#closeStoreSelectorOnly();
    }
  };

  #onDrawerInput = (evt) => {
    const input = evt.target.closest(SEL.searchInput);
    if (input) this.#applySearchDebounced(input.value || '');
  };

  #onConciliationChange = (evt) => {
    const input = evt.target.closest('[data-conciliation-mode]');
    if (!input || input.disabled) return;

    this.pendingConciliationMode = input.dataset.conciliationMode === MODES.CLICK_COLLECT ? MODES.CLICK_COLLECT : MODES.DELIVERY;
    this.pendingConciliationStore =
      this.pendingConciliationMode === MODES.CLICK_COLLECT
        ? this.list.getStoreById(input.dataset.storeId, {
            name: input.dataset.storeName || '',
            address: input.dataset.storeAddress || '',
          })
        : null;
    this.#updateConciliationModeUI();
  };

  #onConciliationClick = (evt) => {
    if (evt.target.closest('[data-action="confirm-conciliation"]')) {
      evt.preventDefault();
      evt.stopPropagation();
      void this.#confirmConciliation();
      return;
    }

    if (evt.target.closest('[data-action="conciliation-switch-store"]')) {
      evt.preventDefault();
      evt.stopPropagation();
      this.storeSelectorOpenedFromTabSwitch = false;
      this.#openStoreSelector();
    }
  };

  #handleConfirmChanges() {
    if (this.pendingTabSwitch) {
      if (this.pendingTabSwitch.mode === MODES.CLICK_COLLECT && !this.pendingTabSwitch.store?.id) {
        this.storeSelectorOpenedFromTabSwitch = true;
        this.#openStoreSelector();
        return;
      }

      void this.#confirmPendingTabSwitch();
      return;
    }

    if (this.state.getMode() === MODES.MIXED) {
      this.#openConciliationPanel();
      return;
    }

    if (this.state.getMode() === MODES.CLICK_COLLECT && !this.state.getSelectedStore()?.id) {
      this.#openStoreSelector();
      return;
    }

    void this.#confirmCheckoutChanges();
  }

  #onCartSummaryClick = (evt) => {
    if (evt.defaultPrevented) return;

    const checkoutButton = evt.target.closest(SEL.checkoutBtn);
    if (checkoutButton) {
      evt.preventDefault();
      evt.stopPropagation();
      evt.stopImmediatePropagation();
      const form = checkoutButton.form || this.#getCheckoutForm();
      void this.#runCheckoutValidation({ form, submitter: checkoutButton });
      return;
    }

    if (evt.target.closest('[data-action="confirm-changes"]')) {
      this.#handleConfirmChanges();
    }
  };

  #onCartFormSubmit = (evt) => {
    const submitter = evt.submitter?.closest?.(SEL.checkoutBtn) || evt.submitter;
    const isCheckoutSubmitter = submitter?.matches?.(SEL.checkoutBtn);
    if (!isCheckoutSubmitter) return;

    if (this.allowNextCheckoutSubmit) {
      this.allowNextCheckoutSubmit = false;
      return;
    }

    evt.preventDefault();
    evt.stopPropagation();
    evt.stopImmediatePropagation();
    void this.#runCheckoutValidation({ form: evt.target, submitter });
  };

  #onCartDrawerKeyup = (evt) => {
    if (evt.key !== 'Escape') return;

    if (this.storePanel && !this.storePanel.hidden) {
      evt.preventDefault();
      evt.stopPropagation();
      evt.stopImmediatePropagation();
      this.#closeStoreSelectorOnly();
      return;
    }

    if (this.conciliationPanel && !this.conciliationPanel.hidden) {
      evt.preventDefault();
      evt.stopPropagation();
      evt.stopImmediatePropagation();
    }
  };

  #onSectionsUpdated = () => {
    const wasSyncing = this.#isSyncFlagActive();
    const hadOpenStorePanel = Boolean(this.storePanel && !this.storePanel.hidden);
    const pendingTabSwitch = this.pendingTabSwitch;
    this.#clearSyncFlag();
    this.inventory.invalidate();
    this.pendingTabSwitch = null;
    this.#refreshDomRefs({ preserveListState: true, rebindPanels: true });
    this.state.restore(this.#getServerSelection());
    this.#syncStateFromRenderedCart();
    if (!wasSyncing && hadOpenStorePanel && pendingTabSwitch?.mode === MODES.CLICK_COLLECT && this.state.getMode() !== MODES.CLICK_COLLECT) {
      this.pendingTabSwitch = pendingTabSwitch;
    }

    if (!wasSyncing && this.state.getMode() === MODES.MIXED) {
      this.pendingConciliationMode = null;
      this.pendingConciliationStore = null;
    }

    if (!wasSyncing) this.#consumeQueuedConciliation();

    this.#syncUI();
    this.#consumeQueuedNotification();

    if (!wasSyncing) this.#openConciliationIfNeeded();

    if (!wasSyncing && hadOpenStorePanel && this.pendingTabSwitch?.mode === MODES.CLICK_COLLECT) {
      this.#openStoreSelector();
    }

    if (this.storePanel && !this.storePanel.hidden) {
      this.#updateStorePanelViews();
    }
  };

  #onOpenStoreSelectorEvent = () => {
    const cartDrawer = document.querySelector('cart-drawer');
    /* Requested from outside the cart (the PLP trigger): the drawer is only a host
       for the panel, so remember to close it again when the selector closes. */
    const opensCartDrawer = Boolean(cartDrawer) && !cartDrawer.hasAttribute('open');
    this.selectorOwnsCartDrawer = opensCartDrawer;

    if (!this.storePanel) {
      if (opensCartDrawer) cartDrawer.open();

      this.#openConciliationPanel();
      return;
    }

    /* Treat the externally requested selector like a pending tab switch so a
       cancel reverts the tabs to the committed cart mode instead of leaving
       the Click & Collect tab active without a store. */
    if (this.state.getMode() !== MODES.CLICK_COLLECT) {
      this.storeSelectorOpenedFromTabSwitch = true;
      this.pendingTabSwitch = { mode: MODES.CLICK_COLLECT, store: null };
      this.#syncUI();
    } else {
      this.storeSelectorOpenedFromTabSwitch = false;
    }

    if (opensCartDrawer) {
      /* Snap the panel open *before* animating the drawer, so the drawer's own
         slide-in reveals the store selector directly. Opening the drawer first and
         sliding the panel over it afterwards flashes the cart behind. */
      this.#openStoreSelector({ animate: false });
      cartDrawer.open();
      return;
    }

    setTimeout(() => this.#openStoreSelector(), TIMING.selectorOpenDelay);
  };

  /* The drawer can also be dismissed by its own close button, the overlay or Escape
     while the selector is open. Drop the panel so reopening the cart shows the cart. */
  #onCartDrawerAfterClose = () => {
    this.selectorOwnsCartDrawer = false;
    /* is-active, not hidden: hidePanel drops the class synchronously but defers
       hidden until transitionend, so this skips our own close path re-entering. */
    if (this.storePanel?.classList.contains('is-active')) this.#closeStoreSelectorOnly();
  };

  #handleTabClick(tab) {
    const mode = tab.dataset.fulfillment;
    if (!mode) return;

    if (this.isSyncingCart) {
      /* The tab component may have flipped the visual state; restore it. */
      this.#syncUI();
      return;
    }

    const currentMode = this.state.getMode();
    const pendingMode = this.pendingTabSwitch?.mode || null;
    if (pendingMode) {
      if (mode === pendingMode) return;

      if (mode === currentMode) {
        this.pendingTabSwitch = null;
        this.pendingTabFocusMode = null;
        this.#syncUI();
        return;
      }
    }

    if (currentMode === MODES.MIXED) {
      this.#openConciliationPanel();
      return;
    }

    if (mode === currentMode) return;

    if (mode === MODES.CLICK_COLLECT) {
      this.#switchToClickCollect();
      return;
    }

    this.#switchToDelivery();
  }

  #switchToClickCollect() {
    /* Never auto-commit a remembered store. Committing writes a store the buyer
       has not chosen for this cart into `_cart_info`, and Shopify locks the pickup
       location from that value - so a stale store here becomes the order's
       fulfillment location. #openStoreSelector pre-highlights the remembered
       store, so confirming it is still a single click. */
    this.storeSelectorOpenedFromTabSwitch = true;
    this.pendingTabSwitch = { mode: MODES.CLICK_COLLECT, store: null };
    this.#syncUI();
    this.#openStoreSelector();
  }

  #switchToDelivery() {
    this.storeSelectorOpenedFromTabSwitch = false;
    if (!this.#isDeliveryCartAllInStock()) {
      this.pendingTabSwitch = { mode: MODES.DELIVERY, store: null };
      this.#syncUI();
      return;
    }

    activateTab(this, MODES.DELIVERY);
    void this.#applyCartFulfillment(MODES.DELIVERY, null, { reconcileUnavailable: true });
  }

  #openStoreSelector({ animate = true } = {}) {
    if (!this.storePanel) {
      this.#openConciliationPanel();
      return;
    }

    if (this.#isConciliationOpen()) {
      this.reopenConciliationAfterStoreSelector = true;
      this.#moveFocusOutOfPanel(this.conciliationPanel);
      this.conciliationPanel.classList.remove('is-active');
      this.conciliationPanel.setAttribute('aria-hidden', 'true');
      this.conciliationPanel.hidden = true;
    }

    this.state.pendingStore = this.state.getSelectedStore()?.id ? null : this.state.getRememberedStore(this.#storeResolver);
    this.#updateStorePanelViews();
    this.list.attach(this.storePanel, { preserveState: true });
    this.list.syncStockFilterToggle();
    if (this.list.userCoords) this.list.applyCoords(this.list.userCoords);
    else this.list.requestGeolocationIfGranted();

    showPanel(this.storePanel, { animate });
    analytics('view_list', {
      source: 'cart',
      store_count: this.list.getStoreItems(this.storePanel).length,
    });
  }

  #closeStoreSelector() {
    if (!this.storePanel) return;

    this.#moveFocusOutOfPanel(this.storePanel);
    hidePanel(this.storePanel);
  }

  #closeStoreSelectorOnly() {
    const openedFromTabSwitch = this.storeSelectorOpenedFromTabSwitch;
    this.storeSelectorOpenedFromTabSwitch = false;
    this.#cancelPendingStore();
    this.#closeStoreSelector();
    this.#updateConciliationModeUI();
    if (this.reopenConciliationAfterStoreSelector) {
      this.reopenConciliationAfterStoreSelector = false;
      this.#openConciliationPanel(false);
      return;
    }

    if (openedFromTabSwitch) {
      this.pendingTabSwitch = null;
      this.pendingTabFocusMode = null;
      this.#syncUI();
    }

    this.#closeCartDrawerIfSelectorOwnsIt();
  }

  /* Only closes the drawer when the selector opened it (i.e. from the PLP trigger);
     inside the cart the drawer is the customer's actual destination. */
  #closeCartDrawerIfSelectorOwnsIt() {
    if (!this.selectorOwnsCartDrawer) return;

    if (this.#isConciliationOpen() || this.reopenConciliationAfterStoreSelector) return;

    this.selectorOwnsCartDrawer = false;
    const cartDrawer = this.cartDrawer || document.querySelector('cart-drawer');
    if (cartDrawer?.hasAttribute('open')) cartDrawer.close();
  }

  #handleStoreRadioChange(radio) {
    const store = readStoreFromItem(radio.closest(SEL.storeItem));
    if (!store) return;

    this.state.pendingStore = store.id === this.state.getSelectedStore()?.id ? null : normalizeStore(store);
    this.#updateStorePanelViews();
  }

  #findStoreCard(storeId) {
    if (!this.storePanel || storeId == null || storeId === '') return null;

    const escapedId = cssEscape(String(storeId));
    return this.storePanel.querySelector(`${SEL.storeItem}[data-store-id="${escapedId}"]`);
  }

  #isStoreCartAllInStock(storeId) {
    return this.inventory.getStoreCartStock(storeId)?.allInStock !== false;
  }

  #isDeliveryCartAllInStock() {
    return this.inventory.getDeliveryCartStock()?.allInStock !== false;
  }

  #isStoreCardAllInStock(storeId) {
    /* The inventory JSON is refreshed on every cart change; card datasets are
       server-rendered and can lag behind quantity edits. */
    if (this.inventory.getInventoryLines().length > 0) return this.#isStoreCartAllInStock(storeId);

    const card = this.#findStoreCard(storeId);
    if (card?.dataset.cartAllInStock !== undefined) return card.dataset.cartAllInStock === 'true';

    return this.#isStoreCartAllInStock(storeId);
  }

  #updateConfirmButton() {
    const footer = $(SEL.storeFooter, this.storePanel);
    const button = $(SEL.confirmStore, this.storePanel);
    if (footer) footer.hidden = false;

    if (!button) return;

    const strings = window.theme.strings.storeSelector || {};
    let confirmAction = 'none';
    let confirmLabel = strings.confirmStore || strings.confirmChanges || button.textContent;
    let enabled = false;
    if (this.state.pendingStore?.id) {
      confirmAction = 'change_store';
      enabled = true;
    } else if (
      this.state.getMode() === MODES.CLICK_COLLECT &&
      this.state.getSelectedStore()?.id &&
      !this.#isStoreCardAllInStock(this.state.getSelectedStore().id)
    ) {
      confirmAction = 'reconcile_current_store';
      confirmLabel = strings.confirmChanges || confirmLabel;
      enabled = true;
    }

    button.dataset.confirmAction = confirmAction;
    button.textContent = confirmLabel;
    button.disabled = !enabled || this.isSyncingCart;
  }

  async #confirmPendingStore() {
    if (this.isSyncingCart) return;

    const confirmAction = $(SEL.confirmStore, this.storePanel)?.dataset.confirmAction || 'none';
    if (confirmAction === 'reconcile_current_store') {
      if (!this.state.getSelectedStore()?.id) return;

      this.#closeStoreSelector();
      await this.#confirmCheckoutChanges();
      this.#closeCartDrawerIfSelectorOwnsIt();
      return;
    }

    if (!this.state.pendingStore) return;

    const store = normalizeStore(this.state.pendingStore);
    if (!store) return;

    const openedFromConciliation = this.reopenConciliationAfterStoreSelector;
    const previousStoreId = this.state.getSelectedStore()?.id || null;
    this.reopenConciliationAfterStoreSelector = false;
    this.state.pendingStore = null;
    this.#closeStoreSelector();
    let conciliationLoader = null;
    if (openedFromConciliation && this.conciliationPanel) {
      this.#openConciliationPanel(false);
      conciliationLoader = this.conciliationPanel.querySelector('.cart-store-conciliation__loader') || null;
      conciliationLoader?.classList.add('is-loading');
    }

    /* Close before the cart round-trip, not after: when the selector opened the
       drawer the customer never asked to see the cart, so leaving it up for the
       duration of the request just flashes it at them. */
    this.#closeCartDrawerIfSelectorOwnsIt();

    const applied = await this.#applyCartFulfillment(MODES.CLICK_COLLECT, store, {
      reconcileUnavailable: true,
      analyticsAction: previousStoreId ? 'change_store' : 'select_store',
      previousStoreId,
      notify: true,
    });
    conciliationLoader?.classList.remove('is-loading');
    if (applied && openedFromConciliation) this.#closeConciliationPanel();

    this.#closeCartDrawerIfSelectorOwnsIt();
  }

  #cancelPendingStore() {
    this.state.pendingStore = null;
    this.#updateStorePanelViews();
  }

  #isConciliationOpen() {
    return Boolean(this.conciliationPanel && !this.conciliationPanel.hidden);
  }

  #getConciliationOptionKey(mode, storeId = '') {
    if (!mode) return '';

    if (mode !== MODES.CLICK_COLLECT) return 'delivery';

    const id = String(storeId || '').trim();
    return id ? `click_collect:${id}` : 'click_collect';
  }

  #openConciliationPanel(resetSelection = true) {
    if (!this.conciliationPanel) return;

    if (this.storePanel && !this.storePanel.hidden) this.#closeStoreSelector();

    this.reopenConciliationAfterStoreSelector = false;
    if (resetSelection) {
      this.pendingConciliationMode = null;
      this.pendingConciliationStore = this.state.getSelectedStore() ? { ...this.state.getSelectedStore() } : null;
    }

    this.#updateConciliationModeUI();
    showPanel(this.conciliationPanel);
  }

  #closeConciliationPanel() {
    if (!this.conciliationPanel) return;

    this.reopenConciliationAfterStoreSelector = false;
    this.#moveFocusOutOfPanel(this.conciliationPanel);
    hidePanel(this.conciliationPanel);
  }

  #updateConciliationModeUI() {
    if (!this.isConnected || !this.conciliationPanel) return;

    const selectedKey = this.#getConciliationOptionKey(this.pendingConciliationMode, this.pendingConciliationStore?.id);
    for (const option of this.conciliationPanel.querySelectorAll('[data-conciliation-option]')) {
      const isSelected = option.dataset.conciliationOption === selectedKey;
      option.classList.toggle('is-selected', isSelected);
      for (const block of option.querySelectorAll('[data-store-affected-items]')) {
        block.hidden = !isSelected || !block.textContent.trim();
      }
    }

    for (const radio of this.conciliationPanel.querySelectorAll('[data-conciliation-mode]')) {
      const optionKey = this.#getConciliationOptionKey(radio.dataset.conciliationMode, radio.dataset.storeId);
      radio.checked = Boolean(selectedKey && optionKey === selectedKey);
    }

    const confirmButton = this.conciliationPanel.querySelector('[data-action="confirm-conciliation"]');
    const clickCollectReady = this.pendingConciliationMode !== MODES.CLICK_COLLECT || Boolean(this.pendingConciliationStore?.id);
    if (confirmButton) {
      confirmButton.disabled = !this.pendingConciliationMode || this.isSyncingCart || !clickCollectReady;
    }
  }

  async #confirmConciliation() {
    if (this.isSyncingCart || !this.pendingConciliationMode) return;

    const store = this.pendingConciliationMode === MODES.CLICK_COLLECT ? this.pendingConciliationStore : null;
    if (this.pendingConciliationMode === MODES.CLICK_COLLECT && !store?.id) {
      this.#updateConciliationModeUI();
      return;
    }

    const loader = this.conciliationPanel?.querySelector('.cart-store-conciliation__loader') || null;
    if (loader) loader.classList.add('is-loading');

    await this.#applyCartFulfillment(this.pendingConciliationMode, store, {
      reconcileUnavailable: true,
      notify: true,
    });
    if (loader) loader.classList.remove('is-loading');

    this.#closeConciliationPanel();
  }

  #getUiMode() {
    return this.pendingTabSwitch?.mode === MODES.CLICK_COLLECT
      ? MODES.CLICK_COLLECT
      : this.pendingTabSwitch?.mode === MODES.DELIVERY
        ? MODES.DELIVERY
        : this.state.getMode();
  }

  #getUiStore() {
    if (this.pendingTabSwitch?.mode === MODES.CLICK_COLLECT) {
      return this.pendingTabSwitch.store || this.state.getSelectedStore();
    }

    if (this.pendingTabSwitch?.mode === MODES.DELIVERY) return null;

    return this.state.getSelectedStore();
  }

  #syncUI() {
    /* After a drawer re-render this instance may have been replaced by a new
       element; writing stale state into the live DOM would fight the new one. */
    if (!this.isConnected) return;

    activateTab(this, this.#getUiMode());
    this.#syncCartHeaderViews();
    this.#syncSummaryInfo();
    this.#syncLineStockBadgeVisibility();
    this.#updatePendingPreview();
    this.#updateCheckoutState();
    this.#updateStorePanelViews();
    this.#updateConciliationModeUI();
  }

  #syncCartHeaderViews() {
    const summary = $(SEL.cartSelectedStoreSummary, this);
    const noStore = $(SEL.cartNoStore, this);
    const uiMode = this.#getUiMode();
    const uiStore = this.#getUiStore();
    const showSummary = uiMode === MODES.CLICK_COLLECT && Boolean(uiStore?.id);
    if (summary) summary.hidden = !showSummary;

    if (noStore) noStore.hidden = uiMode !== MODES.CLICK_COLLECT || showSummary;

    setStoreView(summary, '[data-store-selected-view]', showSummary ? uiStore.id : null);
  }

  #syncSummaryInfo() {
    const summaryInfo = this.cartDrawer?.querySelector('[data-role="click-collect-summary-info"]');
    const spacer = this.cartDrawer?.querySelector('[data-role="click-collect-summary-spacer"]');
    const uiMode = this.#getUiMode();
    const uiStore = this.#getUiStore();
    const showSummary = uiMode === MODES.CLICK_COLLECT && Boolean(uiStore?.id);
    if (summaryInfo) summaryInfo.hidden = !showSummary;

    if (spacer) spacer.hidden = showSummary;
  }

  #syncLineStockBadgeVisibility() {
    const uiMode = this.#getUiMode();
    const uiStore = this.#getUiStore();
    const showBadges = uiMode === MODES.CLICK_COLLECT && Boolean(uiStore?.id);
    for (const badge of this.cartDrawer?.querySelectorAll('[data-role="line-stock"]') || []) {
      badge.hidden = !showBadges;
    }
  }

  #updatePendingPreview() {
    const previewRoot = this.cartDrawer?.querySelector('[data-role="pending-fulfillment-preview"]');
    if (!previewRoot) return;

    const pendingMode = this.pendingTabSwitch?.mode || null;
    let activeBlock = null;
    for (const block of previewRoot.querySelectorAll('[data-preview-kind]')) {
      const matches = pendingMode === MODES.DELIVERY && block.dataset.previewKind === 'delivery';
      block.hidden = !matches;
      if (matches) activeBlock = block;
    }

    previewRoot.hidden = !activeBlock;
  }

  #updateStoreAffectedVisibility(panel, activeStoreId) {
    if (!panel) return;

    const targetId = activeStoreId == null ? null : String(activeStoreId);
    for (const block of panel.querySelectorAll('[data-store-item] [data-store-affected-items]')) {
      const parentItem = block.closest('[data-store-item]');
      const shouldShow = Boolean(
        targetId && block.dataset.storeId === targetId && block.textContent.trim() && parentItem?.dataset.cartAllInStock !== 'true',
      );
      block.hidden = !shouldShow;
    }
  }

  #updateStorePanelViews() {
    if (!this.isConnected || !this.storePanel) return;

    const currentRoot = $(SEL.cartCurrentStoreRoot, this.storePanel);
    const hasSelectedStore = Boolean(this.state.getSelectedStore()?.id);
    if (currentRoot) {
      currentRoot.hidden = !hasSelectedStore;
      setStoreView(currentRoot, '[data-store-current-view]', hasSelectedStore ? this.state.getSelectedStore().id : null);
    }

    const activeStoreId = this.state.pendingStore?.id || this.state.getSelectedStore()?.id || null;
    this.list.highlight(activeStoreId);
    this.#updateStoreAffectedVisibility(this.storePanel, activeStoreId);
    this.#updateConfirmButton();
  }

  #focusCartFallback() {
    const fallback =
      this.cartDrawer?.querySelector('.drawer__close-btn') ||
      this.querySelector('[role="tab"][aria-selected="true"]') ||
      this.querySelector('[role="tab"]');
    if (fallback && typeof fallback.focus === 'function') fallback.focus();
  }

  #moveFocusOutOfPanel(panel) {
    const activeElement = document.activeElement;
    if (!panel || !activeElement || !panel.contains(activeElement)) return;

    if (typeof activeElement.blur === 'function') activeElement.blur();

    this.#focusCartFallback();
  }

  #restorePendingTabFocus() {
    const mode = this.pendingTabFocusMode;
    this.pendingTabFocusMode = null;
    if (!mode) return;

    window.requestAnimationFrame(() => {
      const liveController = document.querySelector('store-selector-cart');
      liveController?.querySelector(`[role="tab"][data-fulfillment="${mode}"]`)?.focus();
    });
  }

  #isStoreStockSatisfied(storeId) {
    /* Trust the live inventory JSON when present; only fall back to the
       server-rendered warning content when no inventory data is available. */
    if (this.inventory.getInventoryLines().length > 0) return this.#isStoreCartAllInStock(storeId);

    const warningRoot = $(SEL.checkoutWarning, this.cartDrawer);
    if (warningRoot) {
      const escapedId = cssEscape(String(storeId));
      const storeWarning = warningRoot.querySelector(`[data-warning-kind="store"][data-store-id="${escapedId}"]`);
      if (storeWarning) {
        const affectedItems = storeWarning.querySelector('[data-role="checkout-warning-affected-items"]');
        return !(affectedItems?.textContent || '').trim();
      }
    }

    return this.#isStoreCardAllInStock(storeId);
  }

  #resolveCheckoutState() {
    if (!this.#hasCartItems()) return { disabled: true, showConfirm: false };

    if (this.pendingTabSwitch) {
      if (this.pendingTabSwitch.mode === MODES.DELIVERY) return { disabled: true, showConfirm: true };

      const pendingStoreId = this.pendingTabSwitch.store?.id;
      const hasOOS = pendingStoreId && !this.#isStoreCartAllInStock(pendingStoreId);
      return hasOOS ? { disabled: true, showConfirm: true, warning: 'store', storeId: pendingStoreId } : { disabled: true, showConfirm: true };
    }

    const mode = this.state.getMode();
    if (mode === MODES.MIXED) return { disabled: true, showConfirm: true, warning: 'mixed' };

    if (mode !== MODES.CLICK_COLLECT) return { disabled: false, showConfirm: false };

    const selectedStoreId = this.state.getSelectedStore()?.id;
    if (!selectedStoreId) return { disabled: true, showConfirm: true, warning: 'missing_store' };

    if (this.isSyncingCart) return { disabled: true, showConfirm: false };

    if (!this.#isStoreStockSatisfied(selectedStoreId)) {
      return { disabled: true, showConfirm: true, warning: 'store', storeId: selectedStoreId };
    }

    return { disabled: false, showConfirm: false };
  }

  #updateCheckoutState() {
    if (!this.isConnected) return;

    const checkoutButton = $(SEL.checkoutBtn, this.cartDrawer);
    const confirmButton = $(SEL.confirmChangesBtn, this.cartDrawer);
    const warningRoot = $(SEL.checkoutWarning, this.cartDrawer);
    if (!checkoutButton) return;

    const { disabled, showConfirm, warning = null, storeId = null } = this.#resolveCheckoutState();
    const strings = window.theme.strings.storeSelector || {};
    if (confirmButton && !confirmButton.dataset.defaultLabel) {
      confirmButton.dataset.defaultLabel = confirmButton.textContent.trim();
    }

    if (!this.isConfirmLoading) {
      checkoutButton.hidden = showConfirm;
      if (confirmButton) {
        confirmButton.hidden = !showConfirm;
        confirmButton.textContent =
          this.pendingTabSwitch?.mode === MODES.DELIVERY
            ? strings.confirmDeliveryMode || strings.confirmChanges || confirmButton.dataset.defaultLabel || confirmButton.textContent
            : strings.confirmChanges || confirmButton.dataset.defaultLabel || confirmButton.textContent;
      }
    }

    checkoutButton.disabled = disabled || this.isSyncingCart || this.isValidatingCheckout;

    if (this.isConfirmLoading) {
      if (warningRoot) warningRoot.hidden = true;
    } else if (warning) {
      this.#getActiveCheckoutWarningBlock(warning, storeId);
    } else if (warningRoot) {
      warningRoot.hidden = true;
    }
  }

  #getActiveCheckoutWarningBlock(kind, storeId = null) {
    const warningRoot = $(SEL.checkoutWarning, this.cartDrawer);
    if (!warningRoot) return null;

    let activeBlock = null;
    for (const block of warningRoot.querySelectorAll('[data-warning-kind]')) {
      const matches =
        kind === 'store'
          ? block.dataset.warningKind === 'store' && String(block.dataset.storeId || '') === String(storeId || '')
          : block.dataset.warningKind === kind;
      block.hidden = !matches;
      if (matches) activeBlock = block;
    }

    warningRoot.hidden = !activeBlock;
    return activeBlock;
  }

  async #confirmCheckoutChanges() {
    if (this.state.getMode() === MODES.MIXED) {
      this.#openConciliationPanel();
      return;
    }

    const store = this.state.getSelectedStore();
    if (this.isSyncingCart || !store?.id) return;

    const confirmBtn = $(SEL.confirmChangesBtn, this.cartDrawer);
    const checkoutBtn = $(SEL.checkoutBtn, this.cartDrawer);
    const activeBtn = confirmBtn && !confirmBtn.hidden ? confirmBtn : checkoutBtn;
    this.isConfirmLoading = true;
    if (activeBtn) activeBtn.classList.add('is-loading');

    /* Run the full orchestrator so the reconciled quantities and the cart
       attributes are committed together — reconciling alone can drop items
       for a store the cart attributes never end up pointing at. */
    await this.#applyCartFulfillment(MODES.CLICK_COLLECT, store, {
      reconcileUnavailable: true,
      notify: true,
    });
    this.isConfirmLoading = false;
    this.#updateCheckoutState();
  }

  async #confirmPendingTabSwitch() {
    if (!this.pendingTabSwitch || this.isSyncingCart) return;

    const { mode, store } = this.pendingTabSwitch;
    this.pendingTabSwitch = null;
    const confirmBtn = $(SEL.confirmChangesBtn, this.cartDrawer);
    const activeBtn = confirmBtn && !confirmBtn.hidden ? confirmBtn : $(SEL.checkoutBtn, this.cartDrawer);
    this.isConfirmLoading = true;
    if (activeBtn) activeBtn.classList.add('is-loading');

    await this.#applyCartFulfillment(mode, store, {
      reconcileUnavailable: true,
      notify: true,
    });
    this.isConfirmLoading = false;
    this.#updateCheckoutState();
  }

  /**
   * Main orchestrator: update state -> reconcile -> sync cart attributes ->
   * refresh drawer.
   *
   * @param {string} mode - Target fulfillment mode.
   * @param {{ id: string, name: string, address: string, phone?: string, salonPhone?: string }|null} store - Target store.
   * @param {object} [options]
   * @param {boolean} [options.reconcileUnavailable] - Remove unavailable items first.
   * @param {string} [options.analyticsAction] - Analytics event name.
   * @param {string|null} [options.previousStoreId] - Previous store ID for analytics.
   * @param {boolean} [options.notify] - Queue a success notification.
   * @returns {Promise<boolean>} Whether the sync succeeded.
   */
  async #applyCartFulfillment(mode, store, options = {}) {
    if (this.isSyncingCart) return false;

    this.pendingTabSwitch = null;
    const nextMode = mode === MODES.CLICK_COLLECT ? MODES.CLICK_COLLECT : MODES.DELIVERY;
    const nextStore = nextMode === MODES.CLICK_COLLECT ? normalizeStore(store) : null;
    if (nextMode === MODES.CLICK_COLLECT && !nextStore?.id) {
      this.#updateConciliationModeUI();
      return false;
    }

    const previousStoreId = options.previousStoreId != null ? options.previousStoreId : this.state.getSelectedStore()?.id || null;
    this.isSyncingCart = true;
    this.#setSyncFlag();
    this.state.mode = nextMode;
    this.state.store = nextStore;
    this.state.pendingStore = null;
    this.pendingConciliationMode = null;
    this.pendingConciliationStore = nextStore ? { ...nextStore } : null;
    if (options.analyticsAction) {
      analytics(options.analyticsAction, {
        source: 'cart',
        store_id: nextStore?.id || null,
        previous_store_id: previousStoreId,
      });
    }

    this.#updateCheckoutState();
    let didReconcile = false;
    if (options.reconcileUnavailable) {
      let reconcileOk = true;
      if (nextMode === MODES.CLICK_COLLECT) {
        if (!this.#isStoreCartAllInStock(nextStore.id)) {
          didReconcile = true;
          reconcileOk = await this.inventory.reconcile({ mode: MODES.CLICK_COLLECT, storeId: nextStore.id });
        }
      } else {
        if (!this.#isDeliveryCartAllInStock()) {
          didReconcile = true;
          reconcileOk = await this.inventory.reconcile({ mode: MODES.DELIVERY });
        }
      }

      if (!reconcileOk) {
        this.isSyncingCart = false;
        this.#clearSyncFlag();
        analytics('error', {
          source: 'cart',
          code: 'reconcile_failed',
        });
        await refreshCartDrawer();
        this.#restorePendingTabFocus();
        this.#showNotification(window.theme.strings.storeSelector.cartSyncError);
        return false;
      }
    }

    const cartAttributeSections = this.cartDrawer?.getSectionsToRender?.().map((section) => section.section) || [];
    const cartAttributeSync = await updateCartAttributes(this.syncQueue, nextMode, nextStore, {
      sections: cartAttributeSections,
      sectionsUrl: window.location.pathname,
    });

    if (!cartAttributeSync?.ok) {
      this.isSyncingCart = false;
      this.#clearSyncFlag();
      analytics('error', {
        source: 'cart',
        code: 'cart_sync_failed',
      });
      await refreshCartDrawer();
      this.#restorePendingTabFocus();
      this.#showNotification(window.theme.strings.storeSelector.cartSyncError);
      return false;
    }

    this.state.setServerSelection(nextMode, nextStore);
    this.state.persist();
    dispatchStoreChanged(nextMode === MODES.CLICK_COLLECT ? nextStore : null, previousStoreId, nextMode, { _source: 'cart-click-collect' });
    if (options.notify) this.#queueNotification();

    if (cartAttributeSync?.cart?.sections && this.cartDrawer?.renderContents) {
      await this.cartDrawer.renderContents(cartAttributeSync.cart, false);
    } else {
      await refreshCartDrawer();
    }

    if (didReconcile) {
      await this.#syncProductCardQuantities();
    }

    this.isSyncingCart = false;
    this.#restorePendingTabFocus();
    return true;
  }

  /**
   * After reconciliation changes cart quantities, notify product card quantity
   * steppers so they reflect the updated cart state.
   */
  async #syncProductCardQuantities() {
    const forms = document.querySelectorAll('product-form[data-show-quantity-selector="true"][data-variant-id]');
    if (!forms.length) return;

    try {
      const res = await fetch(`${window.theme.routes.cart}.js`);
      if (!res.ok) return;

      const cart = await res.json();
      const qtyByVariant = new Map();
      for (const item of cart.items) {
        const vid = String(item.variant_id);
        qtyByVariant.set(vid, (qtyByVariant.get(vid) || 0) + item.quantity);
      }

      for (const form of forms) {
        const vid = form.dataset.variantId;
        const newQty = qtyByVariant.get(vid) || 0;
        document.dispatchEvent(
          new CustomEvent('on:line-item:change', {
            bubbles: true,
            detail: { variantId: vid, newQuantity: newQty },
          }),
        );
      }
    } catch {
      /* no-op */
    }
  }

  #queueNotification() {
    try {
      window.sessionStorage.setItem('cart-click-collect-notification-pending', '1');
    } catch {
      /* no-op */
    }
  }

  #consumeQueuedNotification() {
    try {
      if (window.sessionStorage.getItem('cart-click-collect-notification-pending') !== '1') return;

      window.sessionStorage.removeItem('cart-click-collect-notification-pending');
      this.#showNotification();
    } catch {
      /* no-op */
    }
  }

  #consumeQueuedConciliation() {
    try {
      if (window.sessionStorage.getItem(PENDING_CONCILIATION_STORAGE_KEY) !== '1') return false;

      if (!this.conciliationPanel || !this.#hasCartItems()) return false;

      window.sessionStorage.removeItem(PENDING_CONCILIATION_STORAGE_KEY);
      this.state.mode = MODES.MIXED;
      this.state.store = null;
      this.state.pendingStore = null;
      this.pendingTabSwitch = null;
      this.pendingConciliationMode = null;
      this.pendingConciliationStore = null;
      this.state.setServerSelection(MODES.MIXED, null);
      this.#syncUI();
      this.#openConciliationPanel();
      return true;
    } catch {
      return false;
    }
  }

  #showNotification(messageText) {
    const notification = (this.cartDrawer || this).querySelector('[data-role="click-collect-notification"]');
    const text = notification?.querySelector('[data-role="click-collect-notification-text"]');
    if (!notification) return;

    if (text && messageText) text.textContent = messageText;
    else if (text) text.textContent = window.theme.strings.storeSelector.cartUpdatedNotification;

    notification.hidden = false;
    clearTimeout(this.notificationTimer);
    this.notificationTimer = setTimeout(() => {
      notification.hidden = true;
    }, TIMING.notificationHide);
  }
}

if (!customElements.get('store-selector-cart')) {
  customElements.define('store-selector-cart', StoreSelectorCart);
}

export default StoreSelectorCart;
